import {Box, Button, Loading} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import useSingleCamera from 'src/hooks/api/camera/useSingleCamera'
import useUpdateCamera from 'src/hooks/api/camera/useUpdateCamera'
import CopieIconChangeColor from 'src/images/CopieIcon'
import {AddressInfo} from 'src/services/api'
import {generateLinkRtsp, generateLinkRtspDVR} from 'src/utilities/LinkGenerator'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import Dropdown from '../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../components/dropdown/DropdownProps'
import {Input} from '../../../../components/input/Input'
import CopieIcon from '../../../../images/copieIcon.svg'
import {AccessContainerBox} from '../../../cameras/components/Stepper/CreateCameraStepperBase'
import CameraConfigFooter from '../camera-config-footer/CameraConfigFooter'
import {AccessContainerLastBox, TitleConnection, TitleContainer} from './CameraConfigConnectionBase'

const EnumCameraManufacturer: DropdownItem[] = [
  {label: 'Intelbras', id: '1'},
  {label: 'Hikvision', id: '2'},
]

const streamOptions: DropdownItem[] = [
  {id: '0', label: 'Main-stream'},
  {id: '1', label: 'Sub-stream'},
]

export function CameraConfigConnection() {
  const {enqueueSnackbar} = useSnackbar()
  const {id} = useParams<{id?: string}>()

  const [isEditing, setIsEditing] = useState(false)

  const {mutateAsync: mutateUpdateCamera, isPending: isLoadingUpdateCamera} = useUpdateCamera()

  const {data: camera, isLoading} = useSingleCamera({
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Falha ao tentar obter câmera'),
    cameraId: Number(id),
    params: {includes: ['addressInfo', 'configuration']},
    enabled: !!id,
  })

  const [linkRTSP, setLinkRTSP] = useState<AddressInfo>({} as AddressInfo)

  const [selectedManufacturer, setSelectedManufacturer] = useState({
    id: '',
    label: '',
  })

  const [selectedStream, setSelectedStream] = useState({
    id: '',
    label: '',
  })

  useEffect(() => {
    if (camera?.addressInfo) {
      const isDirectLink = camera?.addressInfo.host == null && camera?.addressInfo.link
      
      isDirectLink 
        ? setLinkRTSP({ link: camera.addressInfo.link } as AddressInfo)
        : setLinkRTSP(camera.addressInfo)

      if (camera?.addressInfo.manufacturer) {
        const manufacturer = EnumCameraManufacturer.find((it) => it.label === camera?.addressInfo.manufacturer)
        if (manufacturer) {
          setSelectedManufacturer(manufacturer)
        }
      }

      if (camera?.addressInfo.stream >= 0) {
        const stremLabel = streamOptions.find((it) => Number(it.id) === camera?.addressInfo.stream)
        if (stremLabel) {
          setSelectedStream(stremLabel)
        }
      }
    }
  }, [camera?.addressInfo])

  const cameraCreatedWithDirectLink = () => {
    if (camera?.addressInfo && camera?.addressInfo.host == null && camera?.addressInfo.link) return true
    return false
  }

  const cameraIsDVR = camera?.origin.includes('DVR')
  const cameraIsDigifort = camera?.origin.includes('Digifort')
  const showDirectLink = cameraCreatedWithDirectLink()

  function maskValue(value: string, max: number): string {
    const numericValue = +value.replace(/\D/g, '')
    const isValid = numericValue <= max && numericValue >= 0
    if (isValid) {
      return numericValue ? String(numericValue) : ''
    } else {
      return value.slice(0, -1)
    }
  }

  const checkIsValidLink = useRef(false)
  const errorMessage = useRef<string | undefined>(undefined)

  const validateLink = () => {
    if (camera?.addressInfo.link) {
      const hasProtocol = camera?.addressInfo.link.includes('://')

      const isValidLink = hasProtocol
      checkIsValidLink.current = isValidLink
      if (!isValidLink) errorMessage.current = 'Link inválido'
      else errorMessage.current = undefined
    } else {
      checkIsValidLink.current = false
      errorMessage.current = 'Link inválido'
    }
  }

  const handleCopieLink = () => {
    if (isEditing) copieEditedLink()
    else {
      if (!camera?.addressInfo.link) return enqueueSnackbar('Ops, você ainda não tem um link para copiar, preencha todos os campos', {variant: 'warning'})

      navigator.clipboard.writeText(camera?.addressInfo.link)
      enqueueSnackbar('Copiado com sucesso', {variant: 'success'})
    }
  }

  const copieEditedLink = () => {
    if (showDirectLink) {
      if (!linkRTSP.link) return
      navigator.clipboard.writeText(linkRTSP.link)
      enqueueSnackbar('Copiado com sucesso', {variant: 'success'})
    } else {
      const link = cameraIsDVR ? generateLinkRtspDVR({addressInfo: linkRTSP, camera}) : generateLinkRtsp({addressInfo: linkRTSP, camera})
      if (link) {
        navigator.clipboard.writeText(link)
        enqueueSnackbar('Copiado com sucesso', {variant: 'success'})
      }
    }
  }

  function handleLinkBeforeUpdate() {
    if (showDirectLink) {
      validateLink()
      return linkRTSP.link
    }
    if (cameraIsDVR) return generateLinkRtspDVR({addressInfo: linkRTSP, camera})
    return generateLinkRtsp({addressInfo: linkRTSP, camera})
  }

  return (
    <Box display='flex' direction='column' rowGap='20px'>
      <Box display='flex' direction='column' align='center' justifyContent='space-between' paddingTop='30px' columnGap='30px'>
        <TitleContainer>
          <TitleConnection>
            <b>Nome da Câmera:</b> {camera?.name}
          </TitleConnection>

          <TitleConnection>
            <b>Origem:</b> {camera?.origin}
          </TitleConnection>
        </TitleContainer>

        {!isLoading && (camera?.addressInfo || cameraIsDigifort) ? (
          <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'center', width: '600px'}}>
            {showDirectLink ? (
              <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '20px', width: '600px'}}>
                <Input.Root
                  maxLength={150}
                  labelFontWeight='400'
                  width='70%'
                  label={`Link ${camera?.origin}`}
                  labelFontSize='12px'
                  defaultValue={camera?.addressInfo.link ?? ''}
                  value={linkRTSP.link || ''}
                  disabled={!isEditing}
                  onChange={(e) => setLinkRTSP({...linkRTSP, link: e.target.value})}
                  error={!checkIsValidLink.current}
                  errorMessage={errorMessage.current}
                />

                <Button fontSize='12px' variant='outlined' disabledTextColor='#8E8E8E' height='40px' width='170px' onClick={() => handleCopieLink()}>
                  <img src={CopieIcon} alt='Copiar link' />
                  {isEditing ? 'Copiar link editado' : `Copiar link ${camera?.origin}`}
                </Button>
              </div>
            ) : (
              <div>
                <AccessContainerBox>
                  <div style={{width: '50%'}}>
                    <Dropdown
                      height='40px'
                      fontSize='12px'
                      label='Fabricante da câmera'
                      labelFontSize='12px'
                      placeholder='Fabricante da câmera'
                      items={EnumCameraManufacturer}
                      disabled={!isEditing}
                      selected={selectedManufacturer || ''}
                      onChangeSelected={(e) => {
                        setSelectedManufacturer(e)
                        setLinkRTSP({...linkRTSP, manufacturer: e.label})
                      }}
                    />
                  </div>

                  <div style={{width: '50%', display: 'flex', gap: '20px'}}>
                    <Dropdown
                      height='40px'
                      fontSize='12px'
                      placeholder='Stream'
                      labelFontSize='12px'
                      label='Stream'
                      disabled={!isEditing}
                      selected={selectedStream || ''}
                      items={streamOptions}
                      onChangeSelected={(e) => {
                        setSelectedStream(e)
                        setLinkRTSP({...linkRTSP, stream: Number(e.id)})
                      }}
                    />
                    <Input.Root
                      maxLength={50}
                      labelFontWeight='400'
                      label='Canal'
                      labelFontSize='12px'
                      type='number'
                      disabled={!isEditing}
                      placeholder='Informe o canal'
                      defaultValue={camera?.addressInfo ? String(camera?.addressInfo.channel) : ''}
                      min={1}
                      mask={(value) => maskValue(value, 100)}
                      value={`${linkRTSP.channel}` || ''}
                      onChange={(e) => setLinkRTSP({...linkRTSP, channel: Number(e.target.value)})}
                    />
                  </div>
                </AccessContainerBox>

                <AccessContainerBox>
                  <div style={{width: '50%'}}>
                    <Input.Root
                      maxLength={50}
                      labelFontWeight='400'
                      placeholder='Insira o host'
                      label='Host'
                      labelFontSize='12px'
                      disabled={!isEditing}
                      defaultValue={camera?.addressInfo ? camera?.addressInfo.host : ''}
                      value={linkRTSP.host}
                      onChange={(e) => setLinkRTSP({...linkRTSP, host: e.target.value})}
                    />
                  </div>

                  <div style={{width: '50%', display: 'flex', gap: '20px'}}>
                    <Input.Root
                      maxLength={50}
                      labelFontWeight='400'
                      labelFontSize='12px'
                      type='number'
                      defaultValue={
                        !cameraIsDigifort && camera?.addressInfo
                          ? String(camera?.addressInfo.rtspPort)
                          : camera?.addressInfo
                            ? String(camera?.addressInfo.httpPort)
                            : ''
                      }
                      placeholder={`Insira a porta ${!cameraIsDigifort ? 'RTSP' : 'HTTP'}`}
                      label={`Porta ${!cameraIsDigifort ? 'RTSP' : 'HTTP'}`}
                      disabled={!isEditing}
                      mask={(value) => maskValue(value, 65535)}
                      value={!cameraIsDigifort ? String(linkRTSP.rtspPort) : String(linkRTSP.httpPort)}
                      onChange={(e) => {
                        if (!cameraIsDigifort) setLinkRTSP({...linkRTSP, rtspPort: Number(e.target.value)})
                        else setLinkRTSP({...linkRTSP, httpPort: Number(e.target.value)})
                      }}
                    />
                  </div>
                </AccessContainerBox>

                <AccessContainerBox>
                  <Input.Root
                    maxLength={50}
                    labelFontWeight='400'
                    placeholder='Insira o usuário'
                    labelFontSize='12px'
                    label='Usuário'
                    defaultValue={camera?.addressInfo ? camera?.addressInfo.user : ''}
                    disabled={!isEditing}
                    value={linkRTSP.user || ''}
                    onChange={(e) => setLinkRTSP({...linkRTSP, user: e.target.value})}
                  />
                  <Input.Root
                    maxLength={50}
                    labelFontWeight='400'
                    placeholder='Insira a senha'
                    labelFontSize='12px'
                    label='Senha'
                    disabled={!isEditing}
                    type='password'
                    hasIcon
                    defaultValue={camera?.addressInfo ? camera?.addressInfo.password : ''}
                    value={linkRTSP.password}
                    onChange={(e) => setLinkRTSP({...linkRTSP, password: e.target.value})}
                  />
                </AccessContainerBox>

                {!cameraIsDigifort && (
                  <AccessContainerLastBox>
                    <Button fontSize='12px' variant='outlined' disabledTextColor='#8E8E8E' height='40px' width='170px' onClick={() => handleCopieLink()}>
                      <CopieIconChangeColor color='#009EFF' />
                      {isEditing ? 'Copiar link editado' : `Copiar link ${camera?.origin}`}
                    </Button>
                  </AccessContainerLastBox>
                )}
              </div>
            )}
          </div>
        ) : (
          <Loading size='20px' />
        )}
      </Box>

      {camera && camera.origin !== 'RTMP' && (
        <CameraConfigFooter
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          isLoading={isLoadingUpdateCamera}
          onCancel={async () => {
            if (camera?.addressInfo) setLinkRTSP(camera?.addressInfo)
            else setLinkRTSP({} as AddressInfo)
            setIsEditing(false)
          }}
          onSubmit={async () => {
            if (!camera) return
            const link = handleLinkBeforeUpdate()
            
            const addressInfoToSend = showDirectLink 
              ? { link } 
              : {
                  channel: linkRTSP.channel,
                  host: linkRTSP.host,
                  httpPort: linkRTSP.httpPort,
                  manufacturer: linkRTSP.manufacturer,
                  password: linkRTSP.password,
                  user: linkRTSP.user,
                  stream: linkRTSP.stream,
                  rtspPort: linkRTSP.rtspPort,
                  link,
                }
            
            await mutateUpdateCamera({
              cameraId: camera.id,
              cameraConfig: camera.configuration,
              addressInfo: addressInfoToSend,
            })

            setIsEditing(false)
          }}
        />
      )}
    </Box>
  )
}
